import { Fragment, type ComponentType, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  FolderTree,
  Handshake,
  Loader2,
  Search,
  Settings2,
  TrendingUp,
  X,
} from "lucide-react";
import {
  createReportExport,
  fetchBusinessDetails,
  fetchIngredientHistory,
  fetchIngredients,
  fetchProcessedInvoices,
  fetchReportWorkspace,
  fetchRecipes,
  type Ingredient,
  type IngredientPricePoint,
  type ReportCategoryRow,
  type ReportDateBasis,
  type ReportExportDataset,
  type ReportExportFormat,
  type ReportLineItemExportRow,
  type ReportSupplierRow,
  type ReportWorkspaceResponse,
  type Recipe,
} from "../../services/apiService";
import { useBusiness } from "../../contexts/BusinessContext";
import { Invoice } from "../../types";
import { getCategoriesForIndustry } from "../../data/categories";
import { InvoiceSlidingPanel } from "./InvoiceListPage";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const FAINT = "#aaa093";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";

const REPORT_TAB_KEYS = ["overview", "costing", "export"] as const;
type TabKey = typeof REPORT_TAB_KEYS[number];

const TAB_META: Record<TabKey, { title: string; subtitle: string }> = {
  overview:   { title: "Reports & Export",    subtitle: "Review spend movement, menu cost changes, margins, and accounting exports." },
  costing:    { title: "Menu Costing Report",  subtitle: "Ingredient price movement and menu item margin analysis." },
  export:     { title: "Export",               subtitle: "Download invoice data and accounting exports for the selected period." },
};
type DatePreset = "all" | "day" | "week" | "month" | "custom";
type SortDir = "asc" | "desc";
type DetailLineSort = "increase" | "decrease" | "price_desc" | "price_asc" | "name";
type SpendHistoryGrain = "date" | "week" | "month";
type ReportPopup = "categories" | "suppliers";
type ReportWorkspaceParams = { startDate: string; endDate: string; dateBasis: ReportDateBasis; includeDuplicates: boolean };
type ReportIconComponent = ComponentType<{ size?: number | string; strokeWidth?: number | string }>;
type AggregatedDetailLine = ReportLineItemExportRow & {
  purchaseCount: number;
  supplierNames: string[];
};

const DATASET_LABELS: Record<ReportExportDataset, string> = {
  invoice_summary: "Invoice summary",
  line_items: "Line items",
  category_spend: "Category spend",
  supplier_spend: "Supplier spend",
};

type ExportColumn = { key: string; label: string };

const SHEET_HINTS: Record<ReportExportDataset, string> = {
  invoice_summary: "One row per invoice",
  line_items: "One row per purchased item",
  category_spend: "Grouped by account category",
  supplier_spend: "Grouped by supplier",
};

const EXPORT_SHEETS: {
  key: ReportExportDataset;
  label: string;
  hint: string;
  icon: ComponentType<{ size?: number | string }>;
}[] = [
  {
    key: "invoice_summary",
    label: DATASET_LABELS.invoice_summary,
    hint: SHEET_HINTS.invoice_summary,
    icon: FileSpreadsheet,
  },
  {
    key: "line_items",
    label: DATASET_LABELS.line_items,
    hint: SHEET_HINTS.line_items,
    icon: FileText,
  },
  {
    key: "category_spend",
    label: DATASET_LABELS.category_spend,
    hint: SHEET_HINTS.category_spend,
    icon: BarChart3,
  },
  {
    key: "supplier_spend",
    label: DATASET_LABELS.supplier_spend,
    hint: SHEET_HINTS.supplier_spend,
    icon: Filter,
  },
];

const DETAIL_LINE_SORT_OPTIONS: { value: DetailLineSort; label: string }[] = [
  { value: "increase", label: "Biggest increase" },
  { value: "decrease", label: "Biggest decrease" },
  { value: "price_desc", label: "Highest purchase" },
  { value: "price_asc", label: "Lowest purchase" },
  { value: "name", label: "Item A-Z" },
];

const SPEND_HISTORY_GRAIN_OPTIONS: { value: SpendHistoryGrain; label: string }[] = [
  { value: "date", label: "Date" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const EXPORT_COLUMNS: Record<ReportExportDataset, ExportColumn[]> = {
  invoice_summary: [
    { key: "invoiceNumber", label: "Invoice #" },
    { key: "supplierName", label: "Supplier" },
    { key: "invoiceDate", label: "Invoice date" },
    { key: "uploadedDate", label: "Uploaded date" },
    { key: "status", label: "Status" },
    { key: "subtotal", label: "Subtotal" },
    { key: "taxAmount", label: "Tax" },
    { key: "totalAmount", label: "Total" },
    { key: "lineItemCount", label: "Line items" },
  ],
  line_items: [
    { key: "invoiceNumber", label: "Invoice #" },
    { key: "supplierName", label: "Supplier" },
    { key: "invoiceDate", label: "Invoice date" },
    { key: "productCode", label: "Product code" },
    { key: "description", label: "Description" },
    { key: "category", label: "Category" },
    { key: "quantity", label: "Quantity" },
    { key: "unit", label: "Unit" },
    { key: "unitPrice", label: "Unit price" },
    { key: "totalPrice", label: "Total" },
  ],
  category_spend: [
    { key: "category", label: "Category" },
    { key: "amount", label: "Spend" },
    { key: "percentOfTotal", label: "% of total" },
    { key: "previousAmount", label: "Previous" },
    { key: "changeAmount", label: "Change $" },
    { key: "changePercent", label: "Change %" },
    { key: "topSupplierName", label: "Top supplier" },
    { key: "invoiceCount", label: "Invoices" },
    { key: "lineItemCount", label: "Line items" },
  ],
  supplier_spend: [
    { key: "supplierName", label: "Supplier" },
    { key: "amount", label: "Spend" },
    { key: "previousAmount", label: "Previous" },
    { key: "changeAmount", label: "Change $" },
    { key: "changePercent", label: "Change %" },
    { key: "invoiceCount", label: "Invoices" },
    { key: "averageInvoiceValue", label: "Avg invoice" },
    { key: "topCategoryName", label: "Top category" },
  ],
};

const DEMO_PREVIEW_ROWS: Record<ReportExportDataset, Record<string, unknown>[]> = {
  invoice_summary: [
    {
      invoiceNumber: "INV-1042",
      supplierName: "Supplier A",
      invoiceDate: "2026-06-24",
      uploadedDate: "2026-06-25",
      status: "complete",
      subtotal: 510,
      taxAmount: 51,
      totalAmount: 561,
      lineItemCount: 8,
    },
    {
      invoiceNumber: "INV-1043",
      supplierName: "Supplier B",
      invoiceDate: "2026-06-25",
      uploadedDate: "2026-06-26",
      status: "complete",
      subtotal: 218,
      taxAmount: 22,
      totalAmount: 240,
      lineItemCount: 5,
    },
  ],
  line_items: [
    {
      invoiceNumber: "INV-1042",
      supplierName: "Supplier A",
      invoiceDate: "2026-06-24",
      productCode: "VEG-014",
      description: "Tomatoes roma 10kg",
      category: "Produce",
      quantity: 2,
      unit: "box",
      unitPrice: 42,
      totalPrice: 84,
    },
    {
      invoiceNumber: "INV-1043",
      supplierName: "Supplier B",
      invoiceDate: "2026-06-25",
      productCode: "BRD-220",
      description: "Sourdough loaf",
      category: "Bakery",
      quantity: 12,
      unit: "each",
      unitPrice: 6,
      totalPrice: 72,
    },
  ],
  category_spend: [
    {
      category: "Produce",
      amount: 1580,
      percentOfTotal: 37.2,
      previousAmount: 1440,
      changeAmount: 140,
      changePercent: 9.7,
      topSupplierName: "Supplier A",
      invoiceCount: 12,
      lineItemCount: 54,
    },
    {
      category: "Bakery",
      amount: 920,
      percentOfTotal: 21.6,
      previousAmount: 1010,
      changeAmount: -90,
      changePercent: -8.9,
      topSupplierName: "Supplier B",
      invoiceCount: 7,
      lineItemCount: 25,
    },
  ],
  supplier_spend: [
    {
      supplierName: "Supplier A",
      amount: 1840,
      previousAmount: 1710,
      changeAmount: 130,
      changePercent: 7.6,
      invoiceCount: 14,
      averageInvoiceValue: 131,
      topCategoryName: "Produce",
    },
    {
      supplierName: "Supplier B",
      amount: 920,
      previousAmount: 1010,
      changeAmount: -90,
      changePercent: -8.9,
      invoiceCount: 7,
      averageInvoiceValue: 131,
      topCategoryName: "Bakery",
    },
  ],
};

function isReportTab(value: string | null): value is TabKey {
  return REPORT_TAB_KEYS.includes(value as TabKey);
}

function localIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthEnd(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function presetRange(preset: DatePreset) {
  const now = new Date();
  if (preset === "all") {
    return { start: "1900-01-01", end: localIsoDate(now) };
  }
  if (preset === "day") {
    return { start: localIsoDate(now), end: localIsoDate(now) };
  }
  if (preset === "week") {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    return { start: localIsoDate(start), end: localIsoDate(now) };
  }
  const start = new Date(now);
  start.setDate(start.getDate() - 30);
  return { start: localIsoDate(start), end: localIsoDate(now) };
}

function inclusiveDayCount(start: string, end: string) {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) return null;
  return Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1;
}

function parseIsoDate(date: string) {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function monthLabel(date: Date) {
  return date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
}

function buildCalendarDays(monthDate: Date) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      iso: localIsoDate(day),
      inMonth: day.getMonth() === month,
    };
  });
}

function buildCalendarWeeks(monthDate: Date) {
  const days = buildCalendarDays(monthDate);
  const weeks = Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7));
  return weeks.filter((week) => week.some((day) => day.inMonth));
}

function DateRangeCalendar({
  start,
  end,
  onChange,
}: {
  start: string;
  end: string;
  onChange: (next: { start: string; end: string }) => void;
}) {
  const initialMonth = parseIsoDate(start) ?? new Date();
  const [viewMonth, setViewMonth] = useState(() => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const todayIso = localIsoDate(new Date());
  const [selectingEnd, setSelectingEnd] = useState(Boolean(start && !end));
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const previewEnd = selectingEnd ? hoverDate : null;
  const rangeEnd = end || previewEnd || "";

  function handleDateClick(iso: string) {
    if (!start || (start && end) || iso < start) {
      onChange({ start: iso, end: "" });
      setSelectingEnd(true);
      setHoverDate(null);
      return;
    }

    onChange({ start, end: iso });
    setSelectingEnd(false);
    setHoverDate(null);
  }

  function dayState(iso: string) {
    const isStart = iso === start;
    const isEnd = Boolean(end && iso === end);
    const isPreviewEnd = Boolean(!end && previewEnd && iso === previewEnd && iso !== start);
    const inRange = Boolean(start && rangeEnd && iso > start && iso < rangeEnd);
    const isToday = iso === todayIso;
    return { isStart, isEnd, isPreviewEnd, inRange, isToday };
  }

  function CalendarMonth({ month }: { month: Date }) {
    return (
      <div className="w-[260px] flex-shrink-0">
        <p className="text-center text-xs font-bold" style={{ color: NAVY }}>{monthLabel(month)}</p>
        <div className="mt-3 grid grid-cols-7 gap-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="h-7 text-center text-[10px] font-bold leading-7" style={{ color: NAVY, opacity: 0.35 }}>
              {day}
            </div>
          ))}
          {buildCalendarWeeks(month).flat().map(({ date, iso, inMonth }) => {
            if (!inMonth) {
              return <div key={iso} className="h-8" />;
            }

            const { isStart, isEnd, isPreviewEnd, inRange, isToday } = dayState(iso);
            const selected = isStart || isEnd || isPreviewEnd;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => handleDateClick(iso)}
                onMouseEnter={() => {
                  if (selectingEnd && start && iso >= start) setHoverDate(iso);
                }}
                className="h-8 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  color: selected ? "white" : NAVY,
                  backgroundColor: selected
                    ? ORANGE
                    : inRange
                      ? "rgba(96,120,64,0.14)"
                      : "transparent",
                  border: isStart && selectingEnd && !end
                    ? `1px solid ${ORANGE}`
                    : isToday && !selected
                      ? `1px solid ${ORANGE}`
                      : "1px solid transparent",
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div onMouseLeave={() => setHoverDate(null)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, -1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Previous month"
        >
          <ChevronLeft size={15} />
        </button>
        <div />
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Next month"
        >
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <CalendarMonth month={viewMonth} />
        <CalendarMonth month={addMonths(viewMonth, 1)} />
      </div>
    </div>
  );
}

function ReportDateRangeControl({
  preset,
  startDate,
  endDate,
  onPresetChange,
  onRangeChange,
}: {
  preset: DatePreset;
  startDate: string;
  endDate: string;
  onPresetChange: (preset: DatePreset) => void;
  onRangeChange: (range: { start: string; end: string }) => void;
}) {
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const customRangeDays = inclusiveDayCount(startDate, endDate);
  const rangeBadgeLabel = customRangeDays === null
    ? `${displayDate(startDate)} - ${displayDate(endDate)}`
    : `${customRangeDays} day${customRangeDays === 1 ? "" : "s"}`;

  useEffect(() => {
    if (!customPickerOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (dateFilterRef.current?.contains(event.target as Node)) return;
      setCustomPickerOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setCustomPickerOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [customPickerOpen]);

  function handlePresetClick(nextPreset: DatePreset) {
    onPresetChange(nextPreset);
    if (nextPreset === "custom") {
      setCustomPickerOpen(true);
      return;
    }
    setCustomPickerOpen(false);
  }

  return (
    <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center lg:justify-end">
      <div className="min-w-0 text-left lg:text-right">
        <p className="text-xs font-semibold" style={{ color: MUTED }}>Date range</p>
      </div>
      <div ref={dateFilterRef} className="relative flex items-center gap-2">
        <Calendar size={15} style={{ color: FAINT }} />
        <div className="flex gap-0.5 rounded-full p-[3px]" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
          {([
            ["all", "All"],
            ["day", "Today"],
            ["week", "7d"],
            ["month", "30d"],
            ["custom", "Custom"],
          ] as [DatePreset, string][]).map(([value, label]) => {
            const active = preset === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => handlePresetClick(value)}
                className="h-7 rounded-full px-3 text-xs font-semibold transition-colors"
                style={active
                  ? { backgroundColor: ACCENT_WEAK, color: ORANGE }
                  : { color: MUTED }
                }
              >
                {label}
              </button>
            );
          })}
        </div>

        {preset === "custom" && customPickerOpen && (
          <div
            className="absolute right-0 top-full z-30 mt-2 rounded-xl p-4"
            style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 18px 44px rgba(80,55,30,0.16)" }}
          >
            <DateRangeCalendar
              start={startDate}
              end={endDate}
              onChange={(range) => {
                onPresetChange("custom");
                onRangeChange(range);
              }}
            />
            <div className="mt-4 flex items-center justify-between gap-4">
              <span className="whitespace-nowrap text-xs font-semibold" style={{ color: MUTED }}>
                {rangeBadgeLabel}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (!endDate) onRangeChange({ start: startDate, end: startDate });
                  setCustomPickerOpen(false);
                }}
                className="rounded-lg px-4 py-2 text-xs font-bold text-white"
                style={{ backgroundColor: ORANGE }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function reportPeriodDescription(
  preset: DatePreset,
  period?: ReportWorkspaceResponse["period"],
  previousPeriod?: ReportWorkspaceResponse["previousPeriod"],
) {
  if (!period) return "Loading selected period...";
  if (preset === "all") return `Showing all invoices through ${displayDate(period.end)}.`;
  return `Comparing ${displayDate(period.start)} - ${displayDate(period.end)} with ${displayDate(previousPeriod?.start)} - ${displayDate(previousPeriod?.end)}.`;
}

function displayDate(value: string | null | undefined) {
  if (!value) return "Not available";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function displayDateTime(value: string | null | undefined) {
  if (!value) return "Not available";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function displayCurrency(value: number | null | undefined) {
  return value === null || value === undefined
    ? "Not available"
    : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(value);
}

function displayNumber(value: number | null | undefined) {
  return value === null || value === undefined ? "0" : value.toLocaleString();
}

function numberOrZeroForSort(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function linePeriodSpend(line: ReportLineItemExportRow) {
  return numberOrZeroForSort(line.currentPeriodSpend ?? line.totalPrice);
}

function normalizeDetailItemName(value: string | null | undefined) {
  return String(value || "Untitled line item").trim().replace(/\s+/g, " ").toLowerCase();
}

function detailSourceKey(line: ReportLineItemExportRow) {
  return [
    (line.supplierId || line.supplierName || "").trim().toLowerCase(),
    (line.productCode || "").trim().toLowerCase(),
    normalizeDetailItemName(line.description),
  ].join("|");
}

function displayPercent(value: number | null | undefined) {
  return value === null || value === undefined ? "N/A" : `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function displayMovementPercent(value: number | null | undefined) {
  return value === null || value === undefined ? "N/A" : `${Math.abs(value).toFixed(1)}%`;
}

const DATE_COLUMN_KEYS = new Set(["invoiceDate", "uploadedDate", "dueDate"]);
const CURRENCY_COLUMN_KEYS = new Set([
  "subtotal",
  "taxAmount",
  "totalAmount",
  "unitPrice",
  "totalPrice",
  "amount",
  "previousAmount",
  "changeAmount",
  "averageInvoiceValue",
]);
const PERCENT_COLUMN_KEYS = new Set(["percentOfTotal", "changePercent"]);
const NUMBER_COLUMN_KEYS = new Set(["lineItemCount", "invoiceCount", "quantity"]);

function datasetRowCount(workspace: ReportWorkspaceResponse, dataset: ReportExportDataset) {
  if (dataset === "invoice_summary") return workspace.invoiceRows.length;
  if (dataset === "line_items") return workspace.lineItemRows.length;
  if (dataset === "category_spend") return workspace.categories.length;
  return workspace.suppliers.length;
}

function previewSupplierNames(workspace: ReportWorkspaceResponse) {
  const names = [
    ...workspace.suppliers.map((row) => row.supplierName),
    ...workspace.invoiceRows.map((row) => row.supplierName),
    ...workspace.lineItemRows.map((row) => row.supplierName),
    ...workspace.categories.map((row) => row.topSupplierName),
  ].filter((name): name is string => Boolean(name && name.trim()));

  return Array.from(new Set(names));
}

function placeholderSupplierName(index: number) {
  return `Supplier ${String.fromCharCode(65 + (index % 26))}`;
}

function demoRowsForDataset(workspace: ReportWorkspaceResponse, dataset: ReportExportDataset) {
  const supplierNames = previewSupplierNames(workspace);
  return DEMO_PREVIEW_ROWS[dataset].map((row, index) => {
    const supplierName = supplierNames[index % supplierNames.length] ?? placeholderSupplierName(index);
    return {
      ...row,
      ...(Object.prototype.hasOwnProperty.call(row, "supplierName") ? { supplierName } : {}),
      ...(Object.prototype.hasOwnProperty.call(row, "topSupplierName") ? { topSupplierName: supplierName } : {}),
    };
  });
}

function previewRowsForDataset(workspace: ReportWorkspaceResponse, dataset: ReportExportDataset): Record<string, unknown>[] {
  const rows = dataset === "invoice_summary"
    ? workspace.invoiceRows.slice(0, 4).map((row) => ({ ...row }))
    : dataset === "line_items"
      ? workspace.lineItemRows.slice(0, 4).map((row) => ({ ...row }))
      : dataset === "category_spend"
        ? workspace.categories.slice(0, 4).map((row) => ({ ...row }))
        : workspace.suppliers.slice(0, 4).map((row) => ({ ...row }));

  return rows.length ? rows : demoRowsForDataset(workspace, dataset);
}

function displayPreviewValue(column: ExportColumn, row: Record<string, unknown>) {
  const value = row[column.key];
  if (value === null || value === undefined || value === "") return "N/A";
  if (DATE_COLUMN_KEYS.has(column.key)) return displayDate(String(value));
  if (PERCENT_COLUMN_KEYS.has(column.key)) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "N/A";
    return column.key === "percentOfTotal" ? `${numeric.toFixed(1)}%` : displayPercent(numeric);
  }
  if (CURRENCY_COLUMN_KEYS.has(column.key)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? displayCurrency(numeric) : "N/A";
  }
  if (NUMBER_COLUMN_KEYS.has(column.key)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? displayNumber(numeric) : "N/A";
  }
  return String(value);
}

function downloadBase64File(base64: string, fileName: string, mimeType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = Array.from(byteCharacters, (character) => character.charCodeAt(0));
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function ChangeBadge({ value, invert = false }: { value: number | null | undefined; invert?: boolean }) {
  if (value === null || value === undefined) {
    return <span className="text-xs font-semibold" style={{ color: MUTED }}>No prior data</span>;
  }
  if (Math.abs(value) < 0.05) {
    return <span className="text-xs font-semibold" style={{ color: MUTED }}>0</span>;
  }
  const isGood = invert ? value <= 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUp : ArrowDown;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold"
      style={{ color: isGood ? DOWN : UP, backgroundColor: isGood ? DOWN_WEAK : UP_WEAK }}
    >
      <Icon size={11} />
      {displayPercent(value)}
    </span>
  );
}

function MoneyChangeBadge({ value, invert = false }: { value: number | null | undefined; invert?: boolean }) {
  if (value === null || value === undefined) {
    return <span className="text-xs font-semibold" style={{ color: MUTED }}>No prior data</span>;
  }
  if (Math.abs(value) < 0.005) {
    return <span className="text-xs font-semibold" style={{ color: MUTED }}>0</span>;
  }
  const isGood = invert ? value <= 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUp : ArrowDown;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold"
      style={{ color: isGood ? DOWN : UP, backgroundColor: isGood ? DOWN_WEAK : UP_WEAK }}
    >
      <Icon size={11} />
      {displayCostCurrency(value)}
    </span>
  );
}

function LineItemPriceMovement({ line }: { line: ReportLineItemExportRow }) {
  const change = line.priceChangePercent;
  const currentSpend = linePeriodSpend(line);
  const hasPrevious = typeof line.previousPeriodSpend === "number" && line.previousPeriodSpend > 0;
  const hasMovement = hasPrevious && typeof change === "number" && Math.abs(change) >= 0.05;
  const isNew = !hasPrevious && currentSpend > 0;
  const color = isNew ? UP : !hasMovement ? NAVY : change > 0 ? UP : DOWN;
  const movementLabel = isNew
    ? "↑"
    : !hasMovement
      ? "0.0%"
      : `${change > 0 ? "↑" : "↓"} ${displayMovementPercent(change)}`;
  const title = isNew
    ? "New in this period: no matching item spend in the previous period"
    : hasPrevious
      ? `Previous period spend ${displayCurrency(line.previousPeriodSpend)} · ${displayPercent(change)}`
      : "No previous item spend in comparison period";

  return (
    <span
      className="flex-shrink-0 text-right text-sm font-bold"
      style={{ color }}
      title={title}
    >
      {displayCurrency(currentSpend)} ({movementLabel})
    </span>
  );
}

function detailLineSubtitle(line: ReportLineItemExportRow | AggregatedDetailLine) {
  const aggregated = line as AggregatedDetailLine;
  if (Array.isArray(aggregated.supplierNames) && aggregated.purchaseCount > 1) {
    const suppliers = aggregated.supplierNames.slice(0, 2).join(", ") || "Multiple suppliers";
    const extra = aggregated.supplierNames.length > 2 ? ` +${aggregated.supplierNames.length - 2}` : "";
    return `${suppliers}${extra} · ${aggregated.purchaseCount} purchases`;
  }
  return `${line.supplierName} - ${line.invoiceNumber || "Missing invoice #"}`;
}

type SpendHistoryRow = {
  key: string;
  label: string;
  sortDate: string;
  amount: number;
  invoiceCount: number;
  averageInvoiceValue: number;
  supplierCount: number;
  topSupplierName: string;
  topSupplierAmount: number;
  changePercent: number | null;
};

function displayShortDate(value: string) {
  const date = parseIsoDate(value);
  if (!date) return displayDate(value);
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function displayCompactDateRange(startIso: string, endIso: string) {
  if (startIso === endIso) return displayShortDate(startIso);
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end) return `${displayDate(startIso)} - ${displayDate(endIso)}`;

  const sameYear = start.getFullYear() === end.getFullYear();
  const startLabel = start.toLocaleDateString("en-AU", sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" });
  const endLabel = end.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  return `${startLabel} - ${endLabel}`;
}

function spendHistoryBucket(value: string, grain: SpendHistoryGrain) {
  const date = parseIsoDate(value);
  if (!date) {
    return { key: "unknown", label: "No invoice date", sortDate: "0000-00-00" };
  }

  if (grain === "date") {
    const iso = localIsoDate(date);
    return { key: iso, label: displayShortDate(iso), sortDate: iso };
  }

  if (grain === "week") {
    const start = new Date(date);
    start.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const startIso = localIsoDate(start);
    const endIso = localIsoDate(end);
    return {
      key: `${startIso}:${endIso}`,
      label: displayCompactDateRange(startIso, endIso),
      sortDate: startIso,
    };
  }

  const start = monthStart(date);
  const startIso = localIsoDate(start);
  return {
    key: startIso.slice(0, 7),
    label: monthLabel(start),
    sortDate: startIso,
  };
}

function spendHistoryPercentChange(current: number, previous: number) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

function buildSpendHistoryRows(
  invoices: Invoice[],
  grain: SpendHistoryGrain,
): SpendHistoryRow[] {
  const map = new Map<string, SpendHistoryRow & { supplierAmounts: Map<string, number> }>();

  for (const invoice of invoices) {
    if (invoice.status === "duplicate") continue;
    const historyDate = invoice.invoiceDate || invoice.uploadedAt?.slice(0, 10) || invoice.receivedDate?.slice(0, 10) || "";
    const bucket = spendHistoryBucket(historyDate, grain);
    const row = map.get(bucket.key) ?? {
      key: bucket.key,
      label: bucket.label,
      sortDate: bucket.sortDate,
      amount: 0,
      invoiceCount: 0,
      averageInvoiceValue: 0,
      supplierCount: 0,
      topSupplierName: "No supplier",
      topSupplierAmount: 0,
      changePercent: null,
      supplierAmounts: new Map<string, number>(),
    };

    const amount = numberOrZeroForSort(invoice.totalAmount);
    const supplierName = invoice.supplier?.name || "Unknown";
    row.amount += amount;
    row.invoiceCount += 1;
    row.supplierAmounts.set(supplierName, (row.supplierAmounts.get(supplierName) || 0) + amount);
    map.set(bucket.key, row);
  }

  const chronologicalRows = Array.from(map.values())
    .sort((a, b) => a.sortDate.localeCompare(b.sortDate))
    .map((row, index, rows) => {
      const topSupplier = Array.from(row.supplierAmounts.entries())
        .sort((a, b) => b[1] - a[1])[0];
      const previous = rows[index - 1];
      return {
        key: row.key,
        label: row.label,
        sortDate: row.sortDate,
        amount: row.amount,
        invoiceCount: row.invoiceCount,
        averageInvoiceValue: row.invoiceCount ? row.amount / row.invoiceCount : 0,
        supplierCount: row.supplierAmounts.size,
        topSupplierName: topSupplier?.[0] || "No supplier",
        topSupplierAmount: topSupplier?.[1] || 0,
        changePercent: previous ? spendHistoryPercentChange(row.amount, previous.amount) : null,
      };
    });

  return chronologicalRows.reverse();
}

function StatTile({
  label,
  value,
  change,
  sub,
  invertChange = false,
}: {
  label: string;
  value: string;
  change?: number | null;
  sub?: string;
  invertChange?: boolean;
}) {
  return (
    <div className="rounded-lg px-4 py-4" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
      <p className="text-xs font-semibold" style={{ color: MUTED }}>{label}</p>
      <p className="mt-2 text-2xl font-semibold leading-none" style={{ color: NAVY, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </p>
      <div className="mt-3 min-h-[24px]">
        {change !== undefined ? <ChangeBadge value={change} invert={invertChange} /> : sub ? <p className="text-xs" style={{ color: MUTED }}>{sub}</p> : null}
      </div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg px-5 py-12 text-center" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
      <FileText size={30} className="mx-auto mb-3" style={{ color: MUTED, opacity: 0.45 }} />
      <p className="text-sm font-semibold" style={{ color: NAVY }}>{title}</p>
      <p className="mt-1 text-xs" style={{ color: MUTED }}>{body}</p>
    </div>
  );
}

function SortButton({
  label,
  active,
  dir,
  onClick,
  align = "left",
  reverseIcon = false,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right" | "center";
  reverseIcon?: boolean;
}) {
  const Icon = reverseIcon
    ? dir === "asc" ? ArrowDown : ArrowUp
    : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"}`}
      style={{ color: active ? ORANGE : MUTED }}
    >
      {label}
      {active && <Icon size={11} />}
    </button>
  );
}

function SpendHistoryTable({ invoices }: { invoices: Invoice[] }) {
  const [grain, setGrain] = useState<SpendHistoryGrain>("week");
  const rows = useMemo(() => buildSpendHistoryRows(invoices, grain), [invoices, grain]);

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
      <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
        <div>
          <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Spending report</h2>
          <p className="text-xs mt-0.5" style={{ color: MUTED }}>All invoice spend grouped by invoice date.</p>
        </div>
        <div className="flex gap-0.5 rounded-full p-[3px]" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
          {SPEND_HISTORY_GRAIN_OPTIONS.map((option) => {
            const active = grain === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setGrain(option.value)}
                className="h-7 rounded-full px-3 text-xs font-semibold transition-colors"
                style={active
                  ? { backgroundColor: ACCENT_WEAK, color: ORANGE }
                  : { color: MUTED }
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px]">
          <thead>
            <tr style={{ borderBottom: `1px solid ${LINE}` }}>
              <th className="px-5 py-3 text-left text-xs font-semibold" style={{ color: MUTED }}>Period</th>
              <th className="px-5 py-3 text-right text-xs font-semibold" style={{ color: MUTED }}>Spend</th>
              <th className="px-5 py-3 text-right text-xs font-semibold" style={{ color: MUTED }}>Invoices</th>
              <th className="px-5 py-3 text-right text-xs font-semibold" style={{ color: MUTED }}>Avg invoice</th>
              <th className="px-5 py-3 text-right text-xs font-semibold" style={{ color: MUTED }}>Suppliers</th>
              <th className="px-5 py-3 text-left text-xs font-semibold" style={{ color: MUTED }}>Top supplier</th>
              <th className="px-5 py-3 text-right text-xs font-semibold" style={{ color: MUTED }}>Vs prior</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} style={{ borderBottom: `1px solid ${LINE}` }}>
                <td className="px-5 py-3 text-sm font-semibold" style={{ color: NAVY }}>{row.label}</td>
                <td className="px-5 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCurrency(row.amount)}</td>
                <td className="px-5 py-3 text-right text-sm" style={{ color: MUTED }}>{displayNumber(row.invoiceCount)}</td>
                <td className="px-5 py-3 text-right text-sm" style={{ color: MUTED }}>{displayCurrency(row.averageInvoiceValue)}</td>
                <td className="px-5 py-3 text-right text-sm" style={{ color: MUTED }}>{displayNumber(row.supplierCount)}</td>
                <td className="px-5 py-3 text-sm" style={{ color: MUTED }}>
                  <span className="block truncate">{row.topSupplierName} ({displayCurrency(row.topSupplierAmount)})</span>
                </td>
                <td className="px-5 py-3 text-right"><ChangeBadge value={row.changePercent} invert /></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-sm" style={{ color: MUTED }}>No spending report available.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportLauncherBox({
  title,
  icon: Icon,
  description,
  onOpen,
}: {
  title: string;
  icon: ReportIconComponent;
  description: string;
  onOpen: () => void;
}) {
  return (
    <div
      className="flex min-h-[108px] items-center justify-between gap-5 rounded-lg px-5 py-4"
      style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}
    >
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center" style={{ color: ORANGE }}>
          <Icon size={30} strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold" style={{ color: NAVY }}>{title}</h2>
          <p className="mt-1 max-w-[520px] text-sm leading-5" style={{ color: MUTED }}>{description}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpen}
        className="inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-md px-4 text-xs font-bold"
        style={{ color: "white", backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
      >
        Open report
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function ReportDetailModal({
  kind,
  businessId,
  onInvoice,
  onClose,
}: {
  kind: ReportPopup;
  businessId?: string;
  onInvoice: (invoiceId: string) => void;
  onClose: () => void;
}) {
  const [datePreset, setDatePreset] = useState<DatePreset>("week");
  const [range, setRange] = useState(() => presetRange("week"));
  const title = kind === "categories" ? "Category report" : "Supplier report";
  const workspaceParams: ReportWorkspaceParams = {
    startDate: range.start,
    endDate: range.end,
    dateBasis: "invoiceDate",
    includeDuplicates: false,
  };
  const workspaceQuery = useQuery<ReportWorkspaceResponse>({
    queryKey: ["report-workspace", businessId, workspaceParams],
    queryFn: () => fetchReportWorkspace(workspaceParams, businessId),
    enabled: Boolean(businessId && range.start && range.end),
  });
  const periodDescription = reportPeriodDescription(
    datePreset,
    workspaceQuery.data?.period,
    workspaceQuery.data?.previousPeriod,
  );

  function handlePresetChange(preset: DatePreset) {
    setDatePreset(preset);
    if (preset === "custom") return;
    setRange(presetRange(preset));
  }

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-5"
      style={{ backgroundColor: "rgba(42,36,32,0.32)", zIndex: 9999 }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <div
        className="flex h-[90vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-xl"
        style={{ backgroundColor: CANVAS, border: `1px solid ${LINE}`, boxShadow: "0 24px 80px rgba(42,36,32,0.22)" }}
      >
        <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
          <div className="min-w-0">
            <h2 className="text-base font-semibold" style={{ color: NAVY }}>{title}</h2>
            <p className="mt-0.5 text-xs" style={{ color: MUTED }}>{periodDescription}</p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between lg:w-auto lg:flex-shrink-0 lg:justify-end">
            <ReportDateRangeControl
              preset={datePreset}
              startDate={range.start}
              endDate={range.end}
              onPresetChange={handlePresetChange}
              onRangeChange={setRange}
            />
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center self-end rounded-full sm:self-auto"
              style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}` }}
              title="Close"
              aria-label="Close report"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {workspaceQuery.isLoading ? (
            <div className="rounded-lg px-5 py-20 text-center" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
              <Loader2 size={26} className="mx-auto animate-spin" style={{ color: ORANGE }} />
              <p className="mt-3 text-sm font-semibold" style={{ color: NAVY }}>Loading report...</p>
            </div>
          ) : workspaceQuery.error ? (
            <div className="rounded-lg px-5 py-4 text-sm" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP_LINE}` }}>
              Could not load report: {(workspaceQuery.error as Error).message}
            </div>
          ) : workspaceQuery.data ? (
            kind === "categories" ? (
              <CategoriesTab workspace={workspaceQuery.data} onInvoice={onInvoice} />
            ) : (
              <SuppliersTab workspace={workspaceQuery.data} onInvoice={onInvoice} />
            )
          ) : (
            <EmptyState title="No report data" body="Choose a period to review this report." />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function OverviewTab({
  invoices,
  businessId,
  onInvoice,
}: {
  invoices: Invoice[];
  businessId?: string;
  onInvoice: (invoiceId: string) => void;
}) {
  const [openReport, setOpenReport] = useState<ReportPopup | null>(null);
  function handleReportInvoice(invoiceId: string) {
    setOpenReport(null);
    onInvoice(invoiceId);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ReportLauncherBox
          title="Category report"
          icon={FolderTree}
          description="Review category spend movement and line-item changes for the selected period."
          onOpen={() => setOpenReport("categories")}
        />
        <ReportLauncherBox
          title="Supplier report"
          icon={Handshake}
          description="Review supplier spend movement, invoice concentration, and line-item detail."
          onOpen={() => setOpenReport("suppliers")}
        />
      </div>

      <SpendHistoryTable invoices={invoices} />

      {openReport && (
        <ReportDetailModal
          kind={openReport}
          businessId={businessId}
          onInvoice={handleReportInvoice}
          onClose={() => setOpenReport(null)}
        />
      )}
    </div>
  );
}

function CategoriesTab({
  workspace,
  onInvoice,
}: {
  workspace: ReportWorkspaceResponse;
  onInvoice: (invoiceId: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: keyof ReportCategoryRow; dir: SortDir }>({ key: "amount", dir: "desc" });
  const [detailSort, setDetailSort] = useState<DetailLineSort>("increase");

  function setSortKey(key: keyof ReportCategoryRow) {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  }

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...workspace.categories]
      .filter((row) => !needle || row.category.toLowerCase().includes(needle) || row.topSupplierName.toLowerCase().includes(needle))
      .sort((a, b) => {
        const av = a[sort.key as keyof typeof a];
        const bv = b[sort.key as keyof typeof b];
        const result = typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? result : -result;
      });
  }, [search, sort, workspace.categories]);

  const selectedRow = rows.find((row) => row.category === selected) ?? rows[0] ?? null;
  const detailLines = useMemo<AggregatedDetailLine[]>(() => {
    if (!selectedRow) return [];
    const map = new Map<string, AggregatedDetailLine>();
    const seenSources = new Map<string, Set<string>>();
    for (const line of workspace.lineItemRows) {
      if (line.category !== selectedRow.category) continue;
      const key = normalizeDetailItemName(line.description);
      const sourceKey = detailSourceKey(line);
      const currentSpend = linePeriodSpend(line);
      const previousSpend = numberOrZeroForSort(line.previousPeriodSpend);
      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          ...line,
          description: line.description || "Untitled line item",
          currentPeriodSpend: currentSpend,
          previousPeriodSpend: previousSpend > 0 ? previousSpend : null,
          priceChangeAmount: previousSpend > 0 ? currentSpend - previousSpend : null,
          priceChangePercent: previousSpend > 0 ? ((currentSpend - previousSpend) / previousSpend) * 100 : null,
          purchaseCount: 1,
          supplierNames: line.supplierName ? [line.supplierName] : [],
        });
        seenSources.set(key, new Set([sourceKey]));
        continue;
      }

      existing.purchaseCount += 1;
      if (line.supplierName && !existing.supplierNames.includes(line.supplierName)) {
        existing.supplierNames.push(line.supplierName);
      }

      const sourceSet = seenSources.get(key) || new Set<string>();
      if (!sourceSet.has(sourceKey)) {
        sourceSet.add(sourceKey);
        seenSources.set(key, sourceSet);
        const nextCurrent = numberOrZeroForSort(existing.currentPeriodSpend) + currentSpend;
        const nextPrevious = numberOrZeroForSort(existing.previousPeriodSpend) + previousSpend;
        existing.currentPeriodSpend = nextCurrent;
        existing.previousPeriodSpend = nextPrevious > 0 ? nextPrevious : null;
        existing.priceChangeAmount = nextPrevious > 0 ? nextCurrent - nextPrevious : null;
        existing.priceChangePercent = nextPrevious > 0 ? ((nextCurrent - nextPrevious) / nextPrevious) * 100 : null;
      }

      if (currentSpend > linePeriodSpend(existing)) {
        existing.invoiceId = line.invoiceId;
        existing.invoiceNumber = line.invoiceNumber;
        existing.supplierId = line.supplierId;
        existing.supplierName = line.supplierName;
        existing.invoiceDate = line.invoiceDate;
      }
    }
    return Array.from(map.values());
  }, [selectedRow, workspace.lineItemRows]);
  const sortedDetailLines = useMemo(() => {
    const changeValue = (line: ReportLineItemExportRow) =>
      typeof line.priceChangePercent === "number"
        ? line.priceChangePercent
        : line.previousPeriodSpend === null || line.previousPeriodSpend === undefined
          ? Number.POSITIVE_INFINITY
          : null;

    return [...detailLines].sort((a, b) => {
      if (detailSort === "name") {
        return (a.description || "").localeCompare(b.description || "");
      }
      if (detailSort === "price_desc" || detailSort === "price_asc") {
        const result = linePeriodSpend(a) - linePeriodSpend(b);
        return detailSort === "price_asc" ? result : -result;
      }

      const aChange = changeValue(a);
      const bChange = changeValue(b);
      if (aChange === null && bChange === null) return (a.description || "").localeCompare(b.description || "");
      if (aChange === null) return 1;
      if (bChange === null) return -1;
      if (!Number.isFinite(aChange) && !Number.isFinite(bChange)) return linePeriodSpend(b) - linePeriodSpend(a);
      if (!Number.isFinite(aChange)) return detailSort === "increase" ? -1 : 1;
      if (!Number.isFinite(bChange)) return detailSort === "increase" ? 1 : -1;
      return detailSort === "increase" ? bChange - aChange : aChange - bChange;
    });
  }, [detailLines, detailSort]);

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(680px,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]">
      <div className="min-w-0 rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
        <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Category report</h2>
            <p className="text-xs mt-0.5" style={{ color: MUTED }}>Line-item spend by category against the previous period.</p>
          </div>
          <label className="relative w-full md:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search categories"
              className="w-full rounded-md py-2 pl-9 pr-3 text-sm outline-none"
              style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
            />
          </label>
        </div>
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                <th className="w-[22%] px-4 py-3 text-left text-xs font-semibold"><SortButton label="Category" active={sort.key === "category"} dir={sort.dir} onClick={() => setSortKey("category")} /></th>
                <th className="w-[13%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Spend" active={sort.key === "amount"} dir={sort.dir} onClick={() => setSortKey("amount")} align="right" /></th>
                <th className="w-[12%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="% total" active={sort.key === "percentOfTotal"} dir={sort.dir} onClick={() => setSortKey("percentOfTotal")} align="right" /></th>
                <th className="w-[14%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Previous" active={sort.key === "previousAmount"} dir={sort.dir} onClick={() => setSortKey("previousAmount")} align="right" /></th>
                <th className="w-[14%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Change" active={sort.key === "changeAmount"} dir={sort.dir} onClick={() => setSortKey("changeAmount")} align="right" reverseIcon /></th>
                <th className="w-[25%] px-4 py-3 text-left text-xs font-semibold" style={{ color: MUTED }}>Top supplier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.category}
                  onClick={() => setSelected(row.category)}
                  className="cursor-pointer"
                  style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: selectedRow?.category === row.category ? ACCENT_WEAK : undefined }}
                >
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}><span className="block truncate">{row.category}</span></td>
                  <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCurrency(row.amount)}</td>
                  <td className="px-4 py-3 text-right text-sm" style={{ color: MUTED }}>{row.percentOfTotal.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right text-sm" style={{ color: MUTED }}>{displayCurrency(row.previousAmount)}</td>
                  <td className="px-4 py-3 text-right"><ChangeBadge value={row.changePercent} invert /></td>
                  <td className="px-4 py-3 text-sm" style={{ color: MUTED }}><span className="block truncate">{row.topSupplierName}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="min-w-0 rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
        {selectedRow ? (
          <>
            <div className="flex flex-col gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
              <div>
                <h2 className="text-sm font-semibold" style={{ color: NAVY }}>{selectedRow.category}</h2>
                <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                  Top supplier: {selectedRow.topSupplierName || "Not available"}.
                </p>
              </div>
              <label className="relative block">
                <ArrowUpDown size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
                <select
                  value={detailSort}
                  onChange={(event) => setDetailSort(event.target.value as DetailLineSort)}
                  className="h-9 w-full appearance-none rounded-md pl-8 pr-8 text-xs font-semibold outline-none"
                  style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
                >
                  {DETAIL_LINE_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="max-h-[560px] overflow-y-auto">
              {sortedDetailLines.slice(0, 80).map((line, index) => (
                <button
                  key={`${line.invoiceId}-${line.productCode}-${index}`}
                  type="button"
                  onClick={() => onInvoice(line.invoiceId)}
                  className="flex w-full items-start justify-between gap-3 px-5 py-3 text-left"
                  style={{ borderBottom: `1px solid ${LINE}` }}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold" style={{ color: NAVY }}>{line.description || "Untitled line item"}</span>
                    <span className="mt-1 block truncate text-xs" style={{ color: MUTED }}>{detailLineSubtitle(line)}</span>
                  </span>
                  <LineItemPriceMovement line={line} />
                </button>
              ))}
              {detailLines.length === 0 && <p className="px-5 py-8 text-sm" style={{ color: MUTED }}>No line items in this category.</p>}
            </div>
          </>
        ) : (
          <EmptyState title="No categories" body="Category spend will appear when this period has invoice line items." />
        )}
      </div>
    </div>
  );
}

function SuppliersTab({
  workspace,
  onInvoice,
}: {
  workspace: ReportWorkspaceResponse;
  onInvoice: (invoiceId: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: keyof ReportSupplierRow; dir: SortDir }>({ key: "amount", dir: "desc" });

  function setSortKey(key: keyof ReportSupplierRow) {
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  }

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...workspace.suppliers]
      .filter((row) => !needle || row.supplierName.toLowerCase().includes(needle) || row.topCategoryName.toLowerCase().includes(needle))
      .sort((a, b) => {
        const av = a[sort.key as keyof typeof a];
        const bv = b[sort.key as keyof typeof b];
        const result = typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? result : -result;
      });
  }, [search, sort, workspace.suppliers]);

  const selectedRow = rows.find((row) => row.supplierId === selected || row.supplierName === selected) ?? rows[0] ?? null;
  const detailLines = selectedRow
    ? workspace.lineItemRows
      .filter((line) => selectedRow.invoiceIds.includes(line.invoiceId))
      .sort((a, b) => numberOrZeroForSort(b.totalPrice) - numberOrZeroForSort(a.totalPrice))
    : [];

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px] 2xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0 rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
        <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Supplier report</h2>
            <p className="text-xs mt-0.5" style={{ color: MUTED }}>Supplier spend, average invoice size, and category concentration.</p>
          </div>
          <label className="relative w-full md:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search suppliers"
              className="w-full rounded-md py-2 pl-9 pr-3 text-sm outline-none"
              style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
            />
          </label>
        </div>
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                <th className="w-[27%] px-4 py-3 text-left text-xs font-semibold"><SortButton label="Supplier" active={sort.key === "supplierName"} dir={sort.dir} onClick={() => setSortKey("supplierName")} /></th>
                <th className="w-[15%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Spend" active={sort.key === "amount"} dir={sort.dir} onClick={() => setSortKey("amount")} align="right" /></th>
                <th className="w-[15%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Previous" active={sort.key === "previousAmount"} dir={sort.dir} onClick={() => setSortKey("previousAmount")} align="right" /></th>
                <th className="w-[13%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Change" active={sort.key === "changeAmount"} dir={sort.dir} onClick={() => setSortKey("changeAmount")} align="right" reverseIcon /></th>
                <th className="w-[15%] px-4 py-3 text-right text-xs font-semibold"><SortButton label="Avg invoice" active={sort.key === "averageInvoiceValue"} dir={sort.dir} onClick={() => setSortKey("averageInvoiceValue")} align="right" /></th>
                <th className="w-[15%] px-4 py-3 text-left text-xs font-semibold" style={{ color: MUTED }}>Top category</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.supplierId || row.supplierName}
                  onClick={() => setSelected(row.supplierId || row.supplierName)}
                  className="cursor-pointer"
                  style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: selectedRow?.supplierName === row.supplierName ? ACCENT_WEAK : undefined }}
                >
                  <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}><span className="block truncate">{row.supplierName}</span></td>
                  <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCurrency(row.amount)}</td>
                  <td className="px-4 py-3 text-right text-sm" style={{ color: MUTED }}>{displayCurrency(row.previousAmount)}</td>
                  <td className="px-4 py-3 text-right"><ChangeBadge value={row.changePercent} invert /></td>
                  <td className="px-4 py-3 text-right text-sm" style={{ color: NAVY }}>{displayCurrency(row.averageInvoiceValue)}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: MUTED }}><span className="block truncate">{row.topCategoryName}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="min-w-0 rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
        {selectedRow ? (
          <>
            <div className="px-5 py-4" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
              <h2 className="text-sm font-semibold" style={{ color: NAVY }}>{selectedRow.supplierName}</h2>
              <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                Top category: {selectedRow.topCategoryName || "Not available"}.
              </p>
            </div>
            <div className="max-h-[560px] overflow-y-auto">
              {detailLines.map((line, index) => (
                <button
                  key={`${line.invoiceId}-${line.productCode}-${index}`}
                  type="button"
                  onClick={() => onInvoice(line.invoiceId)}
                  className="flex w-full items-start justify-between gap-3 px-5 py-3 text-left"
                  style={{ borderBottom: `1px solid ${LINE}` }}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold" style={{ color: NAVY }}>{line.description || "Untitled line item"}</span>
                    <span className="mt-1 block truncate text-xs" style={{ color: MUTED }}>
                      {line.category || "Other"} - {line.invoiceNumber || "Missing invoice #"} - {displayDate(line.invoiceDate)}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-sm font-bold" style={{ color: NAVY }}>{displayCurrency(line.totalPrice)}</span>
                </button>
              ))}
              {detailLines.length === 0 && <p className="px-5 py-8 text-sm" style={{ color: MUTED }}>No line items for this supplier.</p>}
            </div>
          </>
        ) : (
          <EmptyState title="No suppliers" body="Supplier spend will appear when this period has invoices." />
        )}
      </div>
    </div>
  );
}

const COST_UNITS: Record<string, { base: string; factor: number }> = {
  g: { base: "mass", factor: 1 },
  kg: { base: "mass", factor: 1000 },
  oz: { base: "mass", factor: 28.35 },
  lb: { base: "mass", factor: 453.59 },
  mL: { base: "volume", factor: 1 },
  L: { base: "volume", factor: 1000 },
  tsp: { base: "volume", factor: 4.929 },
  tbsp: { base: "volume", factor: 14.787 },
  cup: { base: "volume", factor: 236.588 },
  "fl oz": { base: "volume", factor: 29.574 },
  ea: { base: "ea", factor: 1 },
  piece: { base: "piece", factor: 1 },
  portion: { base: "portion", factor: 1 },
  serving: { base: "serving", factor: 1 },
  batch: { base: "batch", factor: 1 },
};

function displayCostCurrency(value: number | null | undefined) {
  return value === null || value === undefined
    ? "N/A"
    : new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 2 }).format(value);
}

function convertCostUnit(cost: number, from: string, to: string): number | null {
  if (from === to) return cost;
  const source = COST_UNITS[from];
  const target = COST_UNITS[to];
  if (!source || !target || source.base !== target.base) return null;
  return cost * (target.factor / source.factor);
}

function reportUsageCost(quantity: number, usageUnit: string, costPerSourceUnit: number, sourceUnit: string): number | null {
  const rate = convertCostUnit(costPerSourceUnit, sourceUnit, usageUnit);
  return rate === null ? null : quantity * rate;
}

type RecipeCostMode = "current" | "previous";
type RecipeCostResult = { value: number | null; warning: string | null; hasPrior: boolean };

function recipeCostPerUnit(
  recipe: Recipe,
  allRecipes: Recipe[],
  ingredientMap: Map<string, Ingredient>,
  mode: RecipeCostMode,
  visited = new Set<string>(),
): RecipeCostResult {
  if (visited.has(recipe.id)) return { value: null, warning: "Circular recipe reference", hasPrior: false };
  if (recipe.yieldQty <= 0) return { value: null, warning: "Yield must be greater than zero", hasPrior: false };
  visited.add(recipe.id);

  let total = 0;
  let hasPrior = false;
  for (const component of recipe.components) {
    if (component.refType === "ingredient") {
      const ingredient = ingredientMap.get(component.refId);
      if (!ingredient) return { value: null, warning: `${component.name} is not mapped to an ingredient`, hasPrior };
      const price = mode === "previous"
        ? ingredient.previousPrice ?? ingredient.currentPrice
        : ingredient.currentPrice;
      if (price === null || price === undefined) return { value: null, warning: `${ingredient.name} has no current price`, hasPrior };
      hasPrior = hasPrior || ingredient.previousPrice !== null && ingredient.previousPrice !== undefined;
      const cost = reportUsageCost(component.quantity, component.unit, price, ingredient.canonicalUnit);
      if (cost === null) return { value: null, warning: `Unit mismatch on ${ingredient.name}`, hasPrior };
      total += cost;
      continue;
    }

    const subRecipe = allRecipes.find((item) => item.id === component.refId);
    if (!subRecipe) return { value: null, warning: `${component.name} was not found`, hasPrior };
    const subResult = recipeCostPerUnit(subRecipe, allRecipes, ingredientMap, mode, new Set(visited));
    if (subResult.value === null) return { value: null, warning: subResult.warning, hasPrior: hasPrior || subResult.hasPrior };
    hasPrior = hasPrior || subResult.hasPrior;
    const cost = reportUsageCost(component.quantity, component.unit, subResult.value, subRecipe.yieldUnit);
    if (cost === null) return { value: null, warning: `Unit mismatch on ${subRecipe.name}`, hasPrior };
    total += cost;
  }

  return { value: total / recipe.yieldQty, warning: null, hasPrior };
}

function priceChangePercent(current: number | null | undefined, previous: number | null | undefined) {
  if (current === null || current === undefined || previous === null || previous === undefined || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

type IngredientCostSortKey = "name" | "category" | "previous" | "current" | "change" | "sources";
type MenuCostSortKey = "name" | "price" | "cost" | "costChange" | "margin" | "marginChange" | "foodCost";

function hasMeaningfulMovement(value: number | null | undefined, threshold: number) {
  return typeof value === "number" && Math.abs(value) >= threshold;
}

function compareMovementValues(
  a: number | null | undefined,
  b: number | null | undefined,
  dir: SortDir,
  threshold: number,
) {
  const aMoved = hasMeaningfulMovement(a, threshold);
  const bMoved = hasMeaningfulMovement(b, threshold);
  if (aMoved !== bMoved) return aMoved ? -1 : 1;
  if (!aMoved && !bMoved) return 0;
  const result = (a ?? 0) - (b ?? 0);
  return dir === "asc" ? result : -result;
}

function IngredientPriceTrendChart({
  ingredient,
  businessId,
}: {
  ingredient: Ingredient;
  businessId?: string;
}) {
  const { data: historyData, isLoading, error } = useQuery<IngredientPricePoint[]>({
    queryKey: ["ingredient-history", ingredient.id, businessId],
    queryFn: () => fetchIngredientHistory(ingredient.id, businessId),
    enabled: Boolean(businessId),
  });
  const data: IngredientPricePoint[] = historyData ?? [];

  const series = data.map((point) => ({
    date: point.date,
    price: point.price,
  }));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-5 text-xs italic" style={{ color: MUTED }}>
        <Loader2 size={14} className="animate-spin" style={{ color: ORANGE }} />
        Loading price trend...
      </div>
    );
  }

  if (error) {
    return (
      <p className="px-3 py-5 text-xs" style={{ color: UP }}>
        Could not load price trend: {(error as Error).message}
      </p>
    );
  }

  if (series.length === 0) {
    return (
      <p className="px-3 py-5 text-xs italic" style={{ color: MUTED }}>
        No price history yet. Prices appear here after supplier items are mapped and invoices arrive.
      </p>
    );
  }

  return (
    <div className="max-w-full overflow-hidden px-3 py-3">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: NAVY }}>{ingredient.name}</p>
          <p className="mt-0.5 text-xs" style={{ color: MUTED }}>Price trend per {ingredient.canonicalUnit}</p>
        </div>
        <p className="text-xs font-semibold" style={{ color: MUTED }}>
          {series.length} price point{series.length === 1 ? "" : "s"}
        </p>
      </div>
      <ResponsiveContainer width="100%" height={128}>
        <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 8, fill: MUTED }} tickFormatter={(date) => String(date).slice(5)} />
          <YAxis tick={{ fontSize: 8, fill: MUTED }} width={42} tickFormatter={(value) => `$${Number(value).toFixed(1)}`} />
          <Tooltip
            formatter={(value: number) => [`${displayCostCurrency(Number(value))}/${ingredient.canonicalUnit}`, "Price"]}
            labelFormatter={(label) => String(label)}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${LINE}` }}
          />
          <Line
            type="monotone"
            dataKey="price"
            stroke={ORANGE}
            strokeWidth={1.5}
            isAnimationActive={false}
            dot={{ r: 3, fill: ORANGE, stroke: "#fff", strokeWidth: 1 }}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CostingReportTab({ businessId }: { businessId?: string }) {
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const [expandedIngredientId, setExpandedIngredientId] = useState<string | null>(null);
  const [ingredientSort, setIngredientSort] = useState<{ key: IngredientCostSortKey; dir: SortDir }>({ key: "change", dir: "desc" });
  const [menuSort, setMenuSort] = useState<{ key: MenuCostSortKey; dir: SortDir }>({ key: "costChange", dir: "desc" });
  const { data: ingredientsData, isLoading: ingredientsLoading, error: ingredientsError } = useQuery<Ingredient[]>({
    queryKey: ["ingredients", businessId],
    queryFn: () => fetchIngredients(businessId),
    enabled: Boolean(businessId),
  });
  const ingredients: Ingredient[] = ingredientsData ?? [];
  const { data: recipesData, isLoading: recipesLoading, error: recipesError } = useQuery<Recipe[]>({
    queryKey: ["recipes", businessId],
    queryFn: () => fetchRecipes(businessId),
    enabled: Boolean(businessId),
  });
  const recipes: Recipe[] = recipesData ?? [];

  const ingredientRows = useMemo(() => {
    return ingredients
      .map((ingredient) => {
        const current = ingredient.currentPrice;
        const previous = ingredient.previousPrice;
        const changeAmount = current !== null && current !== undefined && previous !== null && previous !== undefined
          ? current - previous
          : null;
        return {
          ingredient,
          current,
          previous,
          changeAmount,
          changePercent: priceChangePercent(current, previous),
        };
      });
  }, [ingredients]);

  const menuRows = useMemo(() => {
    const ingredientMap = new Map(ingredients.map((ingredient) => [ingredient.id, ingredient]));
    return recipes
      .filter((recipe) => recipe.recipeType === "menu_item")
      .map((recipe) => {
        const currentUnit = recipeCostPerUnit(recipe, recipes, ingredientMap, "current");
        const previousUnit = recipeCostPerUnit(recipe, recipes, ingredientMap, "previous");
        const currentCost = currentUnit.value === null ? null : currentUnit.value * recipe.yieldQty;
        const previousCost = previousUnit.value === null || !previousUnit.hasPrior ? null : previousUnit.value * recipe.yieldQty;
        const sellingPrice = recipe.sellingPrice ?? 0;
        const currentMargin = currentCost === null || sellingPrice <= 0 ? null : sellingPrice - currentCost;
        const previousMargin = previousCost === null || sellingPrice <= 0 ? null : sellingPrice - previousCost;
        const foodCostPercent = currentCost === null || sellingPrice <= 0 ? null : (currentCost / sellingPrice) * 100;
        return {
          recipe,
          currentCost,
          previousCost,
          costChange: currentCost !== null && previousCost !== null ? currentCost - previousCost : null,
          costChangePercent: priceChangePercent(currentCost, previousCost),
          currentMargin,
          marginChange: currentMargin !== null && previousMargin !== null ? currentMargin - previousMargin : null,
          foodCostPercent,
          warning: currentUnit.warning,
        };
      });
  }, [ingredients, recipes]);

  function setIngredientSortKey(key: IngredientCostSortKey) {
    setIngredientSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  }

  function setMenuSortKey(key: MenuCostSortKey) {
    setMenuSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  }

  const visibleIngredientRows = useMemo(() => {
    const needle = ingredientSearch.trim().toLowerCase();
    const valueFor = (row: (typeof ingredientRows)[number]) => {
      if (ingredientSort.key === "name") return row.ingredient.name;
      if (ingredientSort.key === "category") return row.ingredient.category || "Uncategorised";
      if (ingredientSort.key === "previous") return row.previous;
      if (ingredientSort.key === "current") return row.current;
      if (ingredientSort.key === "change") return row.changePercent;
      return row.ingredient.sourceCount;
    };

    return ingredientRows
      .filter((row) => {
        if (!needle) return true;
        return [
          row.ingredient.name,
          row.ingredient.category || "Uncategorised",
          row.ingredient.canonicalUnit,
        ].some((value) => value.toLowerCase().includes(needle));
      })
      .sort((a, b) => {
        if (ingredientSort.key === "change") {
          return compareMovementValues(a.changePercent, b.changePercent, ingredientSort.dir, 0.05);
        }
        const av = valueFor(a);
        const bv = valueFor(b);
        const result = typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av ?? "").localeCompare(String(bv ?? ""));
        return ingredientSort.dir === "asc" ? result : -result;
      });
  }, [ingredientRows, ingredientSearch, ingredientSort]);

  const visibleMenuRows = useMemo(() => {
    const needle = menuSearch.trim().toLowerCase();
    const valueFor = (row: (typeof menuRows)[number]) => {
      if (menuSort.key === "name") return row.recipe.name || "Unnamed menu item";
      if (menuSort.key === "price") return row.recipe.sellingPrice;
      if (menuSort.key === "cost") return row.currentCost;
      if (menuSort.key === "costChange") return row.costChangePercent;
      if (menuSort.key === "margin") return row.currentMargin;
      if (menuSort.key === "marginChange") return row.marginChange;
      return row.foodCostPercent;
    };

    return menuRows
      .filter((row) => {
        if (!needle) return true;
        return [
          row.recipe.name || "Unnamed menu item",
          row.recipe.category || "Uncategorised",
          row.warning || "",
        ].some((value) => value.toLowerCase().includes(needle));
      })
      .sort((a, b) => {
        if (menuSort.key === "costChange") {
          return compareMovementValues(a.costChangePercent, b.costChangePercent, menuSort.dir, 0.05);
        }
        if (menuSort.key === "marginChange") {
          return compareMovementValues(a.marginChange, b.marginChange, menuSort.dir, 0.005);
        }
        const av = valueFor(a);
        const bv = valueFor(b);
        const result = typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av ?? "").localeCompare(String(bv ?? ""));
        return menuSort.dir === "asc" ? result : -result;
      });
  }, [menuRows, menuSearch, menuSort]);

  const averageIngredientChange = useMemo(() => {
    const values = ingredientRows.map((row) => row.changePercent).filter((value): value is number => typeof value === "number");
    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }, [ingredientRows]);
  const marginImpact = menuRows.reduce((sum, row) => sum + numberOrZeroForSort(row.marginChange), 0);
  const overTargetCount = menuRows.filter((row) => {
    const target = row.recipe.targetFoodCostPct > 0 ? row.recipe.targetFoodCostPct : 30;
    return row.foodCostPercent !== null && row.foodCostPercent > target;
  }).length;
  const loading = ingredientsLoading || recipesLoading;
  const error = ingredientsError || recipesError;

  if (loading) {
    return (
      <div className="rounded-lg px-5 py-20 text-center" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
        <Loader2 size={26} className="mx-auto animate-spin" style={{ color: ORANGE }} />
        <p className="mt-3 text-sm font-semibold" style={{ color: NAVY }}>Loading cost reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg px-5 py-4 text-sm" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP_LINE}` }}>
        Could not load cost reports: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatTile label="Tracked ingredients" value={displayNumber(ingredients.length)} sub={`${ingredientRows.filter((row) => row.changePercent !== null).length} with prior price`} />
        <StatTile label="Avg ingredient move" value={averageIngredientChange === null ? "N/A" : displayPercent(averageIngredientChange)} sub="Current vs previous" />
        <StatTile label="Menu items" value={displayNumber(menuRows.length)} sub={`${overTargetCount} above target food cost`} invertChange />
        <StatTile label="Margin movement" value={displayCostCurrency(marginImpact)} sub="Known ingredient changes" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
          <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
            <div>
              <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Ingredient price movement</h2>
            </div>
            <label className="relative w-full md:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
              <input
                value={ingredientSearch}
                onChange={(event) => setIngredientSearch(event.target.value)}
                placeholder="Search ingredients"
                className="w-full rounded-md py-2 pl-9 pr-3 text-sm outline-none"
                style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
              />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead>
                <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                  <th className="px-4 py-3 text-left text-xs font-semibold"><SortButton label="Ingredient" active={ingredientSort.key === "name"} dir={ingredientSort.dir} onClick={() => setIngredientSortKey("name")} /></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold"><SortButton label="Category" active={ingredientSort.key === "category"} dir={ingredientSort.dir} onClick={() => setIngredientSortKey("category")} /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Previous" active={ingredientSort.key === "previous"} dir={ingredientSort.dir} onClick={() => setIngredientSortKey("previous")} align="right" /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Current" active={ingredientSort.key === "current"} dir={ingredientSort.dir} onClick={() => setIngredientSortKey("current")} align="right" /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Change" active={ingredientSort.key === "change"} dir={ingredientSort.dir} onClick={() => setIngredientSortKey("change")} align="right" reverseIcon /></th>
                  <th className="px-4 py-3 text-center text-xs font-semibold" style={{ color: MUTED }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {visibleIngredientRows.slice(0, 80).map((row) => {
                  const expanded = expandedIngredientId === row.ingredient.id;
                  return (
                    <Fragment key={row.ingredient.id}>
                      <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                        <td className="px-4 py-3 text-sm font-bold" style={{ color: NAVY }}>{row.ingredient.name}</td>
                        <td className="px-4 py-3 text-sm" style={{ color: MUTED }}>{row.ingredient.category || "Uncategorised"}</td>
                        <td className="px-4 py-3 text-right text-sm" style={{ color: MUTED }}>{displayCostCurrency(row.previous)} /{row.ingredient.canonicalUnit}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCostCurrency(row.current)} /{row.ingredient.canonicalUnit}</td>
                        <td className="px-4 py-3 text-right"><ChangeBadge value={row.changePercent} invert /></td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => setExpandedIngredientId(expanded ? null : row.ingredient.id)}
                            aria-label={`${expanded ? "Hide" : "Show"} ${row.ingredient.name} price trend`}
                            aria-expanded={expanded}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                            style={{
                              color: expanded ? ORANGE : MUTED,
                              backgroundColor: expanded ? ACCENT_WEAK : CHIP,
                              border: `1px solid ${expanded ? ACCENT_LINE : LINE}`,
                            }}
                          >
                            <TrendingUp size={15} />
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                          <td colSpan={6} className="p-0" style={{ backgroundColor: RAISED }}>
                            <IngredientPriceTrendChart ingredient={row.ingredient} businessId={businessId} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {visibleIngredientRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-sm" style={{ color: MUTED }}>
                      {ingredientRows.length === 0 ? "No ingredients have been created yet." : "No ingredients match this search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
          <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
            <div>
              <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Menu cost and margin movement</h2>
            </div>
            <label className="relative w-full md:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: MUTED }} />
              <input
                value={menuSearch}
                onChange={(event) => setMenuSearch(event.target.value)}
                placeholder="Search menu items"
                className="w-full rounded-md py-2 pl-9 pr-3 text-sm outline-none"
                style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
              />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr style={{ borderBottom: `1px solid ${LINE}` }}>
                  <th className="px-4 py-3 text-left text-xs font-semibold"><SortButton label="Menu item" active={menuSort.key === "name"} dir={menuSort.dir} onClick={() => setMenuSortKey("name")} /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Price" active={menuSort.key === "price"} dir={menuSort.dir} onClick={() => setMenuSortKey("price")} align="right" /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Plate cost" active={menuSort.key === "cost"} dir={menuSort.dir} onClick={() => setMenuSortKey("cost")} align="right" /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Cost move" active={menuSort.key === "costChange"} dir={menuSort.dir} onClick={() => setMenuSortKey("costChange")} align="right" reverseIcon /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Margin" active={menuSort.key === "margin"} dir={menuSort.dir} onClick={() => setMenuSortKey("margin")} align="right" /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Margin move" active={menuSort.key === "marginChange"} dir={menuSort.dir} onClick={() => setMenuSortKey("marginChange")} align="right" reverseIcon /></th>
                  <th className="px-4 py-3 text-right text-xs font-semibold"><SortButton label="Food cost" active={menuSort.key === "foodCost"} dir={menuSort.dir} onClick={() => setMenuSortKey("foodCost")} align="right" /></th>
                </tr>
              </thead>
              <tbody>
                {visibleMenuRows.slice(0, 80).map((row) => {
                  const target = row.recipe.targetFoodCostPct > 0 ? row.recipe.targetFoodCostPct : 30;
                  const overTarget = row.foodCostPercent !== null && row.foodCostPercent > target;
                  return (
                    <tr key={row.recipe.id} style={{ borderBottom: `1px solid ${LINE}` }}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-bold" style={{ color: NAVY }}>{row.recipe.name || "Unnamed menu item"}</p>
                        <p className="mt-0.5 text-xs" style={{ color: MUTED }}>{row.warning || row.recipe.category || "Uncategorised"}</p>
                      </td>
                      <td className="px-4 py-3 text-right text-sm" style={{ color: MUTED }}>{displayCostCurrency(row.recipe.sellingPrice)}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCostCurrency(row.currentCost)}</td>
                      <td className="px-4 py-3 text-right"><ChangeBadge value={row.costChangePercent} invert /></td>
                      <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: NAVY }}>{displayCostCurrency(row.currentMargin)}</td>
                      <td className="px-4 py-3 text-right"><MoneyChangeBadge value={row.marginChange} /></td>
                      <td className="px-4 py-3 text-right text-sm font-bold" style={{ color: overTarget ? UP : DOWN }}>
                        {row.foodCostPercent === null ? "N/A" : `${row.foodCostPercent.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
                {visibleMenuRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-sm" style={{ color: MUTED }}>
                      {menuRows.length === 0 ? "No menu items have been created yet." : "No menu items match this search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportTab({
  workspace,
  params,
  businessId,
}: {
  workspace: ReportWorkspaceResponse;
  params: ReportWorkspaceParams;
  businessId?: string;
}) {
  const [format, setFormat] = useState<ReportExportFormat>("excel");
  const [activeSheet, setActiveSheet] = useState<ReportExportDataset>("invoice_summary");
  const [selectedSheets, setSelectedSheets] = useState<ReportExportDataset[]>(["invoice_summary"]);
  const [columns, setColumns] = useState<Partial<Record<ReportExportDataset, string[]>>>({});
  const activeColumns = columns[activeSheet] ?? EXPORT_COLUMNS[activeSheet].map((column) => column.key);
  const previewColumns = EXPORT_COLUMNS[activeSheet].filter((column) => activeColumns.includes(column.key));
  const previewRows = previewRowsForDataset(workspace, activeSheet);
  const exportSheets = format === "excel" ? selectedSheets : [activeSheet];

  const exportMutation = useMutation({
    mutationFn: () => {
      return createReportExport({
        ...params,
        format,
        dataset: format === "csv" ? activeSheet : undefined,
        datasets: format === "csv" ? undefined : exportSheets,
        columns: format === "csv" ? activeColumns : columns,
      }, businessId);
    },
    onSuccess: (response) => {
      downloadBase64File(response.base64, response.fileName, response.mimeType);
    },
  });
  const exportDisabled = exportMutation.isPending;

  function toggleColumn(key: string) {
    setColumns((prev) => {
      const existing = prev[activeSheet] ?? EXPORT_COLUMNS[activeSheet].map((column) => column.key);
      const next = existing.includes(key) ? existing.filter((item) => item !== key) : [...existing, key];
      return { ...prev, [activeSheet]: next.length ? next : existing };
    });
  }

  function selectFormat(nextFormat: ReportExportFormat) {
    setFormat(nextFormat);
    if (nextFormat !== "excel") {
      setSelectedSheets([activeSheet]);
    }
  }

  function toggleSheet(nextSheet: ReportExportDataset) {
    if (format !== "excel") {
      setActiveSheet(nextSheet);
      setSelectedSheets([nextSheet]);
      return;
    }

    if (selectedSheets.includes(nextSheet)) {
      if (selectedSheets.length === 1) {
        setActiveSheet(nextSheet);
        return;
      }
      const remaining = selectedSheets.filter((item) => item !== nextSheet);
      setSelectedSheets(remaining);
      setActiveSheet(activeSheet === nextSheet ? remaining[0] : activeSheet);
      return;
    }

    setSelectedSheets((prev) => [...prev, nextSheet]);
    setActiveSheet(nextSheet);
  }

  return (
    <div className="space-y-4">
      <div className="min-w-0 rounded-lg p-5" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
        {format === "excel" && selectedSheets.length > 1 && (
          <div className="mb-3 flex gap-1 overflow-x-auto">
            {selectedSheets.map((selectedSheet) => {
              const active = activeSheet === selectedSheet;
              return (
                <button
                  key={selectedSheet}
                  type="button"
                  onClick={() => setActiveSheet(selectedSheet)}
                  className="h-7 flex-shrink-0 rounded-md px-3 text-xs font-bold"
                  style={{ color: active ? "white" : NAVY, backgroundColor: active ? ORANGE : CHIP, border: `1px solid ${active ? ORANGE : LINE}` }}
                >
                  {DATASET_LABELS[selectedSheet]}
                </button>
              );
            })}
          </div>
        )}
        <div className="overflow-x-auto rounded-lg" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
          <table className="w-full table-fixed" style={{ minWidth: Math.max(previewColumns.length * 150, 700) }}>
            <thead>
              <tr>
                {previewColumns.map((column) => (
                  <th
                    key={column.key}
                    className="border-b border-r px-4 py-3 text-left text-xs font-bold"
                    style={{ color: NAVY, borderColor: LINE, backgroundColor: RAISED }}
                  >
                    <span className="block truncate">{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {previewColumns.map((column) => (
                    <td
                      key={column.key}
                      className="border-b border-r px-4 py-3 text-sm"
                      style={{ color: NAVY, borderColor: LINE, backgroundColor: SURFACE }}
                    >
                      <span className="block truncate">{displayPreviewValue(column, row)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-lg p-4" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
          <div className="flex items-center gap-2">
            <Settings2 size={16} style={{ color: ORANGE }} />
            <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Export builder</h2>
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold" style={{ color: MUTED }}>Format</p>
              <div className="grid grid-cols-3 gap-1.5">
                {(["excel", "csv", "pdf"] as ReportExportFormat[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => selectFormat(item)}
                    className="h-9 rounded-md px-3 text-xs font-bold capitalize"
                    style={{ color: format === item ? "white" : NAVY, backgroundColor: format === item ? ORANGE : CHIP, border: `1px solid ${format === item ? ORANGE : LINE}` }}
                  >
                    {item === "excel" ? "Excel" : item.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold" style={{ color: MUTED }}>Sheet</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {EXPORT_SHEETS.map((option) => {
                  const Icon = option.icon;
                  const active = activeSheet === option.key;
                  const selected = format === "excel" ? selectedSheets.includes(option.key) : active;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => toggleSheet(option.key)}
                      className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left"
                      style={{
                        backgroundColor: selected ? ACCENT_WEAK : RAISED,
                        border: `1px solid ${selected ? ACCENT_LINE : LINE}`,
                        boxShadow: active && selected ? "0 1px 3px rgba(96,120,64,0.10)" : undefined,
                      }}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md"
                          style={{
                            backgroundColor: selected ? "rgba(96,120,64,0.12)" : SURFACE,
                            color: ORANGE,
                          }}
                        >
                          <Icon size={15} />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-bold" style={{ color: NAVY }}>{option.label}</span>
                          <span className="mt-0.5 block truncate text-xs" style={{ color: MUTED }}>
                            {option.hint} - {datasetRowCount(workspace, option.key)} rows
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => exportMutation.mutate()}
              disabled={exportDisabled}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
            >
              {exportMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {exportMutation.isPending ? "Preparing export..." : `Download ${format === "excel" ? "Excel" : format.toUpperCase()}`}
            </button>

            {exportMutation.error && (
              <p className="rounded-md px-3 py-2 text-xs" style={{ color: UP, backgroundColor: UP_WEAK }}>
                {(exportMutation.error as Error).message}
              </p>
            )}

          </div>
        </div>

        <div className="rounded-lg p-4" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Columns</h2>
            <p className="text-xs mt-0.5" style={{ color: MUTED }}>{DATASET_LABELS[activeSheet]} fields</p>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-semibold" style={{ color: MUTED }}>Columns</p>
                <button
                  type="button"
                  onClick={() => setColumns((prev) => ({ ...prev, [activeSheet]: EXPORT_COLUMNS[activeSheet].map((column) => column.key) }))}
                  className="h-7 rounded-md px-3 text-xs font-bold"
                  style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}` }}
                >
                  All
                </button>
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {EXPORT_COLUMNS[activeSheet].map((column) => (
                  <label
                    key={column.key}
                    className="flex items-center gap-2.5 rounded-md px-3 py-1.5"
                    style={{ backgroundColor: activeColumns.includes(column.key) ? ACCENT_WEAK : RAISED, border: `1px solid ${activeColumns.includes(column.key) ? ACCENT_LINE : LINE}` }}
                  >
                    <input
                      type="checkbox"
                      checked={activeColumns.includes(column.key)}
                      onChange={() => toggleColumn(column.key)}
                      style={{ accentColor: ORANGE }}
                    />
                    <span className="text-sm font-semibold" style={{ color: NAVY }}>{column.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { activeBusiness } = useBusiness();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab: TabKey = isReportTab(requestedTab) ? requestedTab : "overview";
  const dateBasis: ReportDateBasis = "invoiceDate";
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const overviewRange = presetRange("all");
  const workspaceParams: ReportWorkspaceParams = {
    startDate: overviewRange.start,
    endDate: overviewRange.end,
    dateBasis,
    includeDuplicates: false,
  };

  const workspaceQuery = useQuery<ReportWorkspaceResponse>({
    queryKey: ["report-workspace", activeBusiness?.id, workspaceParams],
    queryFn: () => fetchReportWorkspace(workspaceParams, activeBusiness?.id),
    enabled: Boolean(activeBusiness && activeTab === "export"),
  });

  const { data: invoicesData } = useQuery<Invoice[]>({
    queryKey: ["invoices", activeBusiness?.id],
    queryFn: () => fetchProcessedInvoices(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const invoices: Invoice[] = invoicesData ?? [];

  const { data: businessDetails } = useQuery({
    queryKey: ["business", activeBusiness?.id],
    queryFn: () => fetchBusinessDetails(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });

  const categories = getCategoriesForIndustry(businessDetails?.industry);
  const selectedInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId) ?? null;

  function closeInvoicePanel() {
    setSelectedInvoiceId(null);
    queryClient.invalidateQueries({ queryKey: ["report-workspace", activeBusiness?.id] });
    queryClient.invalidateQueries({ queryKey: ["invoices", activeBusiness?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-summary", activeBusiness?.id] });
  }

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: CANVAS, fontFamily: "var(--font-ui)" }}>
      <div className="pl-9 pr-6 pt-9 pb-2 flex-shrink-0" style={{ backgroundColor: CANVAS }}>
        <div>
          <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>{TAB_META[activeTab].title}</h1>
          <p className="text-sm mt-0.5" style={{ color: MUTED }}>
            {TAB_META[activeTab].subtitle}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === "overview" ? (
          <OverviewTab invoices={invoices} businessId={activeBusiness?.id} onInvoice={setSelectedInvoiceId} />
        ) : activeTab === "costing" ? (
          <CostingReportTab businessId={activeBusiness?.id} />
        ) : workspaceQuery.isLoading ? (
          <div className="rounded-lg px-5 py-20 text-center" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
            <Loader2 size={26} className="mx-auto animate-spin" style={{ color: ORANGE }} />
            <p className="mt-3 text-sm font-semibold" style={{ color: NAVY }}>Loading report workspace...</p>
          </div>
        ) : workspaceQuery.error ? (
          <div className="rounded-lg px-5 py-4 text-sm" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP_LINE}` }}>
            Could not load report workspace: {(workspaceQuery.error as Error).message}
          </div>
        ) : workspaceQuery.data ? (
          <ExportTab
            workspace={workspaceQuery.data}
            params={workspaceParams}
            businessId={activeBusiness?.id}
          />
        ) : (
          <EmptyState title="No report data" body="Processed invoice spend will appear here." />
        )}
      </div>

      {selectedInvoice && (
        <InvoiceSlidingPanel
          inv={selectedInvoice}
          businessId={activeBusiness?.id}
          categories={categories}
          onClose={closeInvoicePanel}
        />
      )}
    </div>
  );
}
